'use client';

import { Button } from '@ahafood/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@ahafood/ui/components/dialog';
import { ScrollArea } from '@ahafood/ui/components/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@ahafood/ui/components/sheet';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { ArrowLeft } from 'lucide-react';
import { memo, useMemo } from 'react';

import { useAIPlannerSheet } from './ai-planner-context';
import { AIProcessingScreen } from './ai-processing';
import { IntroScreen } from './onboarding/intro-screen';
import { StepAllergies } from './onboarding/step-allergies';
import { StepConfirmReminder } from './onboarding/step-confirm-reminder';
import { StepDeliveryDays } from './onboarding/step-delivery-days';
import { StepDietType } from './onboarding/step-diet-type';
import { StepEatingGoals } from './onboarding/step-eating-goals';
import { StepPersonalInfo } from './onboarding/step-personal-info';
import { PlanOverview } from './plan-overview';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile';
import { useAIPlannerStore } from '@/store/ai-planner';

// const STEP_TITLES: Record<string, string> = {
//   intro: 'AI lên lịch ăn trưa tự động',
//   delivery_days: 'Bạn muốn bữa trưa được giao đến ngày nào?',
//   personal_info: 'Thông tin cá nhân',
//   eating_goals: 'Mục tiêu ăn uống của bạn trưa nay là gì?',
//   diet_type: 'Chế độ ăn cho bữa trưa của bạn là gì?',
//   allergies: 'Bạn có dị ứng hay lưu ý gì đặc biệt?',
//   processing: 'Phân tích kế hoạch ăn',
//   overview: 'Thực đơn ăn trưa của bạn',
// };

const AIPlannerSheetContent = memo(function AIPlannerSheetContent() {
  const currentStep = useAIPlannerStore.use.currentStep();
  const prevStep = useAIPlannerStore.use.prevStep();

  const { closeSheet } = useAIPlannerSheet();

  const handleClose = () => {
    // Track close from intro (popup back)
    trackEvent('meal_plan', {
      button_name: 'is_back',
      button_location: currentStep,
    });
    closeSheet();
  };

  const handlePrevStep = () => {
    if (currentStep === 'intro' || currentStep === 'overview') {
      // Track close from intro (popup back)
      trackEvent('meal_plan', {
        button_name: 'meal_plan_popup_back',
        button_location: 'home_page',
      });
      closeSheet();
    } else {
      trackEvent('meal_plan', {
        button_name: 'meal_plan_back',
        button_location: currentStep,
      });
      prevStep();
    }
  };

  const showBackButton = useMemo(() => {
    return !['processing'].includes(currentStep);
  }, [currentStep]);

  const renderStepContent = () => {
    switch (currentStep) {
      case 'intro':
        return <IntroScreen />;
      case 'delivery_days':
        return <StepDeliveryDays />;
      case 'personal_info':
        return <StepPersonalInfo />;
      case 'eating_goals':
        return <StepEatingGoals />;
      case 'diet_type':
        return <StepDietType />;
      case 'allergies':
        return <StepAllergies />;
      case 'processing':
        return <AIProcessingScreen />;
      case 'overview':
        return <PlanOverview />;
      case 'confirm_reminder':
        return <StepConfirmReminder />;
      default:
        return <IntroScreen />;
    }
  };

  // const isOnboardingStep = ![
  //   'intro',
  //   'delivery_days',
  //   'processing',
  //   'overview',
  // ].includes(currentStep);

  // // For intro step, we show the tab switch header
  // const isIntroStep = currentStep === 'intro';

  return (
    <div className="flex h-full flex-col bg-[#012749]">
      {/* Header */}
      <div className="relative px-4 pt-3 pb-4 bg-white/15 rounded-b-3xl border border-t-0 backdrop-blur-xl overflow-hidden">
        {/* Background Image with Opacity */}
        <div
          className="absolute inset-0 opacity-50 pointer-events-none"
          style={{
            backgroundImage: `url(${assetPrefix}/static/images/ai-planner-header-bg.png)`,
            backgroundRepeat: 'repeat-x',
            backgroundPosition: 'center',
            backgroundSize: 'auto 100%',
          }}
        />

        {/* Back button for non-intro steps */}
        {showBackButton && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-3 top-3 size-7 bg-neutral-10/50 rounded-full text-white/80 hover:text-white hover:bg-white/10 z-50"
            onClick={handlePrevStep}
          >
            <ArrowLeft className="size-5" />
          </Button>
        )}

        {/* Title */}
        <h2 className="text-lg font-semibold text-white text-center pt-1 mb-4 relative z-10">
          AI lên lịch ăn trưa tự động
        </h2>

        {/* Tab Switch for intro step */}
        <div className="relative z-10 flex items-center bg-white/20 rounded-full p-0.5 overflow-hidden border w-auto mx-auto">
          {/* Tab: GIAO NGAY */}
          <button
            onClick={handleClose}
            className="relative z-10 flex-1 flex justify-center items-center gap-2 px-2 py-1.5 rounded-full transition-all duration-200 text-white/60"
          >
            <img
              className="h-5 w-auto"
              src={`${assetPrefix}/static/icons/native-tab-icon.svg`}
              alt="Giao ngay"
            />
            <span className="text-xs font-semibold">GIAO NGAY</span>
          </button>

          {/* Tab: ĂN TRƯA TỰ ĐỘNG (Active) */}
          <button
            disabled
            className="relative z-10 bg-white/30 text-white flex-1 flex justify-center items-center gap-2 px-2 py-1.5 rounded-full transition-all duration-200"
            style={{ backdropFilter: 'blur(24px)' }}
          >
            <img
              className="aspect-square w-7 scale-125 object-cover"
              src={`${assetPrefix}/static/images/ai-planner-tab-icon.webp`}
              alt="Ăn trưa tự động"
            />
            <span className="text-xs font-semibold whitespace-nowrap">
              ĂN TRƯA TỰ ĐỘNG
            </span>
          </button>
        </div>
      </div>

      {/* Progress Indicator for onboarding steps */}
      {/* {isOnboardingStep && (
        <div className="px-4 py-2">
          <StepIndicator />
        </div>
      )} */}

      {/* Content */}
      <ScrollArea className="flex-1 [&_[data-radix-scroll-area-viewport]>div]:!block">
        <div
          className={cn(
            'min-h-full px-4 py-4',
            'animate-in fade-in-0 slide-in-from-bottom-4 duration-300'
          )}
        >
          {renderStepContent()}
        </div>
      </ScrollArea>
    </div>
  );
});

export function AIPlannerSheet() {
  const isMobile = useIsMobile();
  const { isOpen, closeSheet } = useAIPlannerSheet();

  if (!isMobile) {
    return (
      <Dialog
        open={isOpen}
        onOpenChange={(open) => !open && closeSheet()}
        modal={false}
      >
        <DialogContent className="max-w-[480px] h-[90vh] p-0 gap-0 overflow-hidden bg-transparent border-0">
          <DialogHeader className="sr-only">
            <DialogTitle>AI Meal Planner</DialogTitle>
          </DialogHeader>
          <AIPlannerSheetContent />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => !open && closeSheet()}
      // modal={false}
    >
      <SheetContent
        side="bottom"
        className="size-full p-0 overflow-hidden bg-transparent border-0"
        // style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <SheetHeader className="sr-only hidden">
          <SheetTitle>AI Meal Planner</SheetTitle>
        </SheetHeader>
        <AIPlannerSheetContent />
      </SheetContent>
    </Sheet>
  );
}
